import { ChangeEvent, FormEvent, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../redux/store';
import { zenworksServiceApi } from '../../service/zenworksService.api';
import ButtonWithLoading from '../common/ButtomWIthLoading';
import { ADD_EVENT } from '../../redux/reducers/eventReducer';
import getTimeStamp from '../../utils/getTimeStamp';
import { isValidEmail } from '../../utils/validations';
import { generateRandomId } from '../../utils/global.utils';
import { DESCRIPTION_MSG, TRANSFER_AGENT_CONFIG } from './transferAgentConfig';

const INITIAL_STATE = {
  reason: '',
  queryType: '',
  integrationCategory: '',
  integrationSubCategory: '',
  suppliedEmail: '',
  suppliedName: '',
};

type IProps = {
  setShowPopup: (showLogin: boolean) => void;
};

function TransferHumanAgent({ setShowPopup }: IProps) {
  const dispatch = useDispatch<AppDispatch>();
  const isSubmittedRef = useRef(false);
  const emailRef = useRef<HTMLInputElement | null>(null);
  const connectionStatus = useSelector(
    (state: RootState) => state.socketConnection.status,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [formValues, setFormValues] = useState(INITIAL_STATE);
  const [availableSubCategories, setAvailableSubCategories] = useState<{ key: string; label: string }[]>([]);

  // Get query type options from config
  const queryTypeOptions = TRANSFER_AGENT_CONFIG.queryTypes;
  const integrationOptions = TRANSFER_AGENT_CONFIG.integrations;

  const isFormValid =
    formValues.reason.trim() !== '' &&
    isValidEmail(formValues.suppliedEmail) &&
    formValues.queryType.trim() !== '' &&
    (formValues.queryType !== 'Integration Support' ||
      (formValues.integrationCategory.trim() !== '' &&
        (availableSubCategories.length === 0 ||
          formValues.integrationSubCategory.trim() !== '')));

  const handleChange = (
    e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement | HTMLSelectElement>,
  ) => {
    e.preventDefault();
    const { name, value } = e.target;

    // Handle query type change
    if (name === 'queryType') {
      setFormValues((prevForm) => ({
        ...prevForm,
        [name]: value,
        // Clear integration fields if switching away from Integration Support
        ...(value !== 'Integration Support' && {
          integrationCategory: '',
          integrationSubCategory: '',
        }),
      }));
      setAvailableSubCategories([]);
      return;
    }

    // Handle integration category change
    if (name === 'integrationCategory') {
      const selectedIntegration = integrationOptions.find(
        (int) => int.key === value,
      );
      const newSubCategories = selectedIntegration?.subCategories || [];
      setAvailableSubCategories(newSubCategories);
      setFormValues((prevForm) => ({
        ...prevForm,
        [name]: value,
        // Clear sub-category if new selection has no subcategories
        integrationSubCategory: '',
      }));
      return;
    }

    setFormValues((prevForm) => ({ ...prevForm, [name]: value }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (isSubmittedRef.current || isLoading) {
      return;
    }

    if (!isFormValid) {
      return;
    }

    setIsLoading(true);
    isSubmittedRef.current = true;

    const timeStamp = getTimeStamp();
    const loadingDivId = generateRandomId();

    dispatch({
      type: ADD_EVENT,
      payload: {
        type: 'messageCard',
        id: loadingDivId,
        divId: loadingDivId,
        user: 'actionCard',
        time: timeStamp,
        msg: 'Please wait while I process this request...',
        senderType: 'chatBot',
      },
    });

    if (formValues?.suppliedName?.trim()) {
      try {
        localStorage.setItem('name', formValues.suppliedName.trim());
      } catch (e) {
        console.warn('Failed to store name in localStorage:', e);
      }
    }

    const body = {
      SuppliedName: formValues?.suppliedName?.trim() || '',
      SuppliedEmail: formValues.suppliedEmail.trim(),
      Product: 'Tax1099',
      Status: 'New',
      Origin: 'AI Chatbot',
      Type: 'Question',
      Subject: 'Support Request',
      QueryType: formValues.queryType,
      IntegrationCategory:
        formValues.queryType === 'Integration Support'
          ? formValues.integrationCategory
          : "",
      IntegrationSubCategory:
        formValues.queryType === 'Integration Support' &&
          availableSubCategories.length > 0
          ? formValues.integrationSubCategory
          : "",
      Description: formValues.reason.trim(),
    };

    try {
      const res = await zenworksServiceApi.createCase(body);
      const successDivId = generateRandomId();

      dispatch({
        type: ADD_EVENT,
        payload: {
          type: 'messageCard',
          id: successDivId,
          divId: successDivId,
          user: 'actionCard',
          time: getTimeStamp(),
          msg: res.message || 'Your support request has been submitted successfully. Our team will respond shortly.',
          senderType: 'chatBot',
        },
      });

      // Close popup after success
      setTimeout(() => {
        setShowPopup(false);
      }, 1000);
    } catch (error: any) {
      isSubmittedRef.current = false;
      const errorDivId = generateRandomId();
      const errorMessage = error?.message || 'An error occurred while processing your request. Please try again.';

      dispatch({
        type: ADD_EVENT,
        payload: {
          type: 'messageCard',
          id: errorDivId,
          divId: errorDivId,
          user: 'actionCard',
          time: getTimeStamp(),
          msg: errorMessage,
          senderType: 'chatBot',
        },
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    try {
      const name = localStorage.getItem('name');
      const email = localStorage.getItem('email');

      if (name) {
        setFormValues((prev) => ({ ...prev, suppliedName: name }));
      }
      if (email) {
        setFormValues((prev) => ({ ...prev, suppliedEmail: email }));
      }
    } catch (e) {
      console.warn('Failed to load from localStorage:', e);
    }
  }, []);

  return (
    <div className="bg-white rounded-lg p-6">
      <div className="mb-1 font-bold text-left text-gray-900">
        Connect with our Customer Success!
      </div>
      <div className="mb-5 text-left text-gray-700 text-sm">
        Fill in the required details below to submit your support request. Our team will respond within 10 minutes. Thank you.
      </div>
      <div className="mx-0">
        <form onSubmit={handleSubmit}>
          <div className="my-4">
            <div className="my-3 relative z-0 w-full">
              <input
                name="suppliedName"
                type="text"
                id="suppliedName"
                placeholder=" "
                className="block py-2.5 px-0 w-full text-sm text-gray-900 bg-transparent border-0 border-b-2 border-gray-300 appearance-none focus:outline-none focus:ring-0 focus:border-blue-500 peer transition-colors"
                value={formValues.suppliedName}
                onChange={handleChange}
                disabled={isSubmittedRef.current}
              />
              <label
                htmlFor="suppliedName"
                className="peer-focus:font-medium absolute text-sm text-gray-500 origin-left duration-300 transform -translate-y-6 scale-75 top-3 -z-10 origin-0 peer-focus:left-0 peer-focus:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-6"
              >
                Your Name (Optional)
              </label>
            </div>

            <div className="my-3 relative z-0 w-full">
              <input
                ref={emailRef}
                name="suppliedEmail"
                type="email"
                id="suppliedEmail"
                placeholder=" "
                className="block py-2.5 px-0 w-full text-sm text-gray-900 bg-transparent border-0 border-b-2 border-gray-300 appearance-none focus:outline-none focus:ring-0 focus:border-blue-500 peer transition-colors"
                value={formValues.suppliedEmail}
                onChange={handleChange}
                disabled={isSubmittedRef.current}
                required
              />
              <label
                htmlFor="suppliedEmail"
                className="peer-focus:font-medium absolute text-sm text-gray-500 origin-left duration-300 transform -translate-y-6 scale-75 top-3 -z-10 origin-0 peer-focus:left-0 peer-focus:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-6"
              >
                Your Email *
              </label>
            </div>

            {/* Query Type Field */}
            <div className="my-3 relative z-0 w-full">
              <label htmlFor="queryType" className="block text-sm font-medium text-gray-700 mb-2">
                Reason for Contact *
              </label>
              <select
                id="queryType"
                name="queryType"
                value={formValues.queryType}
                className="block appearance-none w-full bg-white border-2 border-gray-300 hover:border-blue-400 px-4 py-2.5 pr-8 rounded-md shadow-sm leading-tight focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed"
                onChange={handleChange}
                disabled={isSubmittedRef.current}
                required
              >
                <option value="">
                  Select the reason you need assistance with
                </option>
                {queryTypeOptions.map((option, index) => (
                  <option key={`query-type-${index}`} value={option.key}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Integration Category Field - Conditional */}
            {formValues.queryType === 'Integration Support' && (
              <div className="my-3 relative z-0 w-full">
                <label htmlFor="integrationCategory" className="block text-sm font-medium text-gray-700 mb-2">
                  Select Integration *
                </label>
                <select
                  id="integrationCategory"
                  name="integrationCategory"
                  value={formValues.integrationCategory}
                  className="block appearance-none w-full bg-white border-2 border-gray-300 hover:border-blue-400 px-4 py-2.5 pr-8 rounded-md shadow-sm leading-tight focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed"
                  onChange={handleChange}
                  disabled={isSubmittedRef.current}
                  required
                >
                  <option value="">
                    Select Integration
                  </option>
                  {integrationOptions.map((integration, index) => (
                    <option
                      key={`integration-${index}`}
                      value={integration.key}
                    >
                      {integration.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Integration Sub-Category Field - Conditional */}
            {formValues.queryType === 'Integration Support' &&
              formValues.integrationCategory &&
              availableSubCategories.length > 0 && (
                <div className="my-3 relative z-0 w-full">
                  <label htmlFor="integrationSubCategory" className="block text-sm font-medium text-gray-700 mb-2">
                    Select Feature Type *
                  </label>
                  <select
                    id="integrationSubCategory"
                    name="integrationSubCategory"
                    value={formValues.integrationSubCategory}
                    className="block appearance-none w-full bg-white border-2 border-gray-300 hover:border-blue-400 px-4 py-2.5 pr-8 rounded-md shadow-sm leading-tight focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed"
                    onChange={handleChange}
                    disabled={isSubmittedRef.current}
                    required
                  >
                    <option value="">
                      Select Feature Type
                    </option>
                    {availableSubCategories.map((subCategory, index) => (
                      <option
                        key={`sub-category-${index}`}
                        value={subCategory.key}
                      >
                        {subCategory.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}

            {/* Message/Reason Field */}
            <div className="my-3 relative z-0 w-full">
              <label
                htmlFor="message"
                className="block mb-2 text-sm font-medium text-gray-700"
              >
                Description of Your Issue *
              </label>
              <textarea
                id="message"
                rows={4}
                name="reason"
                value={formValues.reason}
                onChange={handleChange}
                className="block p-3 w-full text-sm text-gray-900 bg-white rounded-md border border-gray-300 focus:ring-blue-500 focus:border-blue-500 focus:ring-1 transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed resize-none"
                placeholder="Please describe your issue in detail..."
                disabled={isSubmittedRef.current}
                required
              />
            </div>
            {formValues.queryType === 'Integration Support' ? <div>{DESCRIPTION_MSG}</div> : <></>}
          </div>

          <div className="text-right mt-6">
            <ButtonWithLoading
              isLoading={isLoading}
              label="Submit"
              type="submit"
              className={`text-white p-2.5 rounded-lg w-full font-medium transition-all duration-200 ${isFormValid && connectionStatus === 'Connected' && !isSubmittedRef.current
                ? 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800 cursor-pointer'
                : 'bg-gray-400 cursor-not-allowed opacity-60'
                }`}
              disabled={
                !isFormValid ||
                connectionStatus !== 'Connected' ||
                isSubmittedRef.current ||
                isLoading
              }
            />
            {connectionStatus !== 'Connected' && (
              <p className="text-xs text-red-600 mt-2">
                Connection required to submit. Status: {connectionStatus}
              </p>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

export default TransferHumanAgent;